import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import api from '../api';

const STATUS_OPTIONS = [
  { value: 'lead',     label: 'Lead',     bg: '#fef3c7', text: '#d97706' },
  { value: 'active',   label: 'Active',   bg: '#eff6ff', text: '#2563eb' },
  { value: 'client',   label: 'Client',   bg: '#f0fdf4', text: '#16a34a' },
  { value: 'inactive', label: 'Inactive', bg: '#f8fafc', text: '#94a3b8' },
];

const emptyForm = {
  name: '', email: '', phone: '', company: '',
  job_title: '', address: '', status: 'lead', notes: '', project_ids: [],
};

export default function ContactsPage() {
  const [contacts,     setContacts]     = useState([]);
  const [projects,     setProjects]     = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [search,       setSearch]       = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showForm,     setShowForm]     = useState(false);
  const [editContact,  setEditContact]  = useState(null);
  const [form,         setForm]         = useState(emptyForm);
  const [saving,       setSaving]       = useState(false);
  const [error,        setError]        = useState('');
  const [viewContact,  setViewContact]  = useState(null);

  useEffect(() => {
    fetchContacts();
    api.get('/wp/v2/projects?per_page=100').then(r => setProjects(r.data || [])).catch(() => {});
  }, []);

  const fetchContacts = async () => {
    setLoading(true);
    try {
      const res = await api.get('/wp/v2/contacts?per_page=100&orderby=title&order=asc');
      setContacts(res.data || []);
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  };

  const openNew = () => {
    setEditContact(null);
    setForm(emptyForm);
    setError('');
    setShowForm(true);
    setViewContact(null);
  };

  const openEdit = (contact) => {
    setEditContact(contact);
    setForm({
      name:        contact.title?.rendered || '',
      email:       contact.meta?.gpf_email     || '',
      phone:       contact.meta?.gpf_phone     || '',
      company:     contact.meta?.gpf_company   || '',
      job_title:   contact.meta?.gpf_job_title || '',
      address:     contact.meta?.gpf_address   || '',
      status:      contact.meta?.gpf_status    || 'lead',
      notes:       contact.content?.rendered?.replace(/<[^>]*>/g,'') || '',
      project_ids: JSON.parse(contact.meta?.gpf_project_ids || '[]'),
    });
    setError('');
    setShowForm(true);
    setViewContact(null);
  };

  const handleSave = async () => {
    if (!form.name.trim()) { setError('Name is required.'); return; }
    setSaving(true); setError('');
    try {
      const payload = {
        title:   form.name,
        content: form.notes,
        status:  'publish',
        meta: {
          gpf_email:       form.email,
          gpf_phone:       form.phone,
          gpf_company:     form.company,
          gpf_job_title:   form.job_title,
          gpf_address:     form.address,
          gpf_status:      form.status,
          gpf_project_ids: JSON.stringify(form.project_ids),
        }
      };
      if (editContact) {
        await api.put(`/wp/v2/contacts/${editContact.id}`, payload);
      } else {
        await api.post('/wp/v2/contacts', payload);
      }
      setShowForm(false);
      setEditContact(null);
      fetchContacts();
    } catch(e) {
      setError(e.response?.data?.message || 'Failed to save contact.');
    } finally { setSaving(false); }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete contact "${name}"?`)) return;
    try {
      await api.delete(`/wp/v2/contacts/${id}?force=true`);
      setContacts(prev => prev.filter(c => c.id !== id));
      if (viewContact?.id === id) setViewContact(null);
    } catch(e) { alert('Failed to delete contact.'); }
  };

  const getStatusStyle = (status) => STATUS_OPTIONS.find(s => s.value === status) || STATUS_OPTIONS[0];

  const getInitials = (name) => name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0,2) || '?';

  const getProjectName = (id) => projects.find(p => p.id === parseInt(id))?.title?.rendered || '';

  const filtered = contacts.filter(c => {
    const name    = c.title?.rendered?.toLowerCase() || '';
    const company = (c.meta?.gpf_company || '').toLowerCase();
    const email   = (c.meta?.gpf_email   || '').toLowerCase();
    const q       = search.toLowerCase();
    if (filterStatus !== 'all' && c.meta?.gpf_status !== filterStatus) return false;
    if (search && !name.includes(q) && !company.includes(q) && !email.includes(q)) return false;
    return true;
  });

  return (
    <Layout>
      <div style={styles.page}>
        {/* Header */}
        <div style={styles.header}>
          <div>
            <h1 style={styles.title}>Contacts</h1>
            <p style={styles.sub}>{filtered.length} contact{filtered.length !== 1 ? 's' : ''}</p>
          </div>
          <button style={styles.addBtn} onClick={openNew}>+ Add Contact</button>
        </div>

        {/* Filters */}
        <div style={styles.filterBar}>
          <div style={styles.tabs}>
            <button style={{ ...styles.tab, ...(filterStatus === 'all' ? styles.tabActive : {}) }} onClick={() => setFilterStatus('all')}>
              All <span style={styles.tabCount}>{contacts.length}</span>
            </button>
            {STATUS_OPTIONS.map(s => (
              <button key={s.value} style={{ ...styles.tab, ...(filterStatus === s.value ? styles.tabActive : {}) }} onClick={() => setFilterStatus(s.value)}>
                {s.label}
                <span style={styles.tabCount}>{contacts.filter(c => c.meta?.gpf_status === s.value).length}</span>
              </button>
            ))}
          </div>
          <input style={styles.search} placeholder="Search by name, company or email..." value={search} onChange={e => setSearch(e.target.value)} />
        </div>

        {/* Add / Edit Form */}
        {showForm && (
          <div style={styles.formCard}>
            <div style={styles.formHeader}>
              <h3 style={styles.formTitle}>{editContact ? 'Edit Contact' : 'New Contact'}</h3>
              <button style={styles.formClose} onClick={() => setShowForm(false)}>✕</button>
            </div>
            {error && (
              <div style={styles.errorBox}>⚠️ {error}</div>
            )}
            <div style={styles.formGrid}>
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={styles.label}>Full Name *</label>
                <input style={styles.input} value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="Jane Smith" autoFocus />
              </div>
              <div>
                <label style={styles.label}>Email</label>
                <input style={styles.input} type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} placeholder="jane@company.com" />
              </div>
              <div>
                <label style={styles.label}>Phone</label>
                <input style={styles.input} value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} placeholder="+1 (555) 000-0000" />
              </div>
              <div>
                <label style={styles.label}>Company</label>
                <input style={styles.input} value={form.company} onChange={e => setForm({...form, company: e.target.value})} placeholder="Acme Corp" />
              </div>
              <div>
                <label style={styles.label}>Job Title</label>
                <input style={styles.input} value={form.job_title} onChange={e => setForm({...form, job_title: e.target.value})} placeholder="VP of Marketing" />
              </div>
              <div>
                <label style={styles.label}>Address</label>
                <input style={styles.input} value={form.address} onChange={e => setForm({...form, address: e.target.value})} placeholder="City, State, Country" />
              </div>
              <div>
                <label style={styles.label}>Status</label>
                <select style={styles.input} value={form.status} onChange={e => setForm({...form, status: e.target.value})}>
                  {STATUS_OPTIONS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={styles.label}>Link to Projects</label>
                <div style={styles.projectPicker}>
                  {projects.map(p => (
                    <label key={p.id} style={styles.projectCheck}>
                      <input
                        type="checkbox"
                        checked={form.project_ids.includes(p.id)}
                        onChange={e => {
                          const ids = e.target.checked
                            ? [...form.project_ids, p.id]
                            : form.project_ids.filter(id => id !== p.id);
                          setForm({...form, project_ids: ids});
                        }}
                      />
                      <span>{p.title?.rendered}</span>
                    </label>
                  ))}
                  {projects.length === 0 && <span style={{ fontSize: 13, color: '#94a3b8' }}>No projects yet</span>}
                </div>
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={styles.label}>Notes</label>
                <textarea style={{ ...styles.input, minHeight: 80, resize: 'vertical' }} value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} placeholder="Any notes about this contact..." />
              </div>
            </div>
            <div style={styles.formActions}>
              <button style={styles.cancelBtn} onClick={() => setShowForm(false)}>Cancel</button>
              <button style={styles.saveBtn} onClick={handleSave} disabled={saving}>
                {saving ? 'Saving...' : (editContact ? 'Save Changes' : 'Add Contact')}
              </button>
            </div>
          </div>
        )}

        {/* Contact Grid */}
        {loading ? (
          <div style={styles.empty}><p>Loading contacts...</p></div>
        ) : filtered.length === 0 ? (
          <div style={styles.empty}>
            <p style={{ fontSize: 40, margin: 0 }}>👤</p>
            <p style={styles.emptyTitle}>{search || filterStatus !== 'all' ? 'No contacts match your filters' : 'No contacts yet'}</p>
            {!search && filterStatus === 'all' && <button style={styles.addBtn} onClick={openNew}>Add your first contact</button>}
          </div>
        ) : (
          <div style={styles.grid}>
            {filtered.map(contact => {
              const name    = contact.title?.rendered || 'Unnamed';
              const status  = contact.meta?.gpf_status || 'lead';
              const sc      = getStatusStyle(status);
              const projIds = JSON.parse(contact.meta?.gpf_project_ids || '[]');

              return (
                <div key={contact.id} style={styles.card} onClick={() => setViewContact(contact)}>
                  <div style={styles.cardTop}>
                    {/* Avatar */}
                    <div style={styles.avatar}>{getInitials(name)}</div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={styles.contactName}>{name}</div>
                      {contact.meta?.gpf_job_title && <div style={styles.contactRole}>{contact.meta.gpf_job_title}</div>}
                      {contact.meta?.gpf_company && <div style={styles.contactCompany}>🏢 {contact.meta.gpf_company}</div>}
                    </div>
                    <span style={{ ...styles.statusBadge, background: sc.bg, color: sc.text }}>{sc.label}</span>
                  </div>

                  <div style={styles.cardInfo}>
                    {contact.meta?.gpf_email && (
                      <a href={`mailto:${contact.meta.gpf_email}`} style={styles.infoRow} onClick={e => e.stopPropagation()}>
                        <span>✉️</span><span style={styles.infoText}>{contact.meta.gpf_email}</span>
                      </a>
                    )}
                    {contact.meta?.gpf_phone && (
                      <a href={`tel:${contact.meta.gpf_phone}`} style={styles.infoRow} onClick={e => e.stopPropagation()}>
                        <span>📞</span><span style={styles.infoText}>{contact.meta.gpf_phone}</span>
                      </a>
                    )}
                    {contact.meta?.gpf_address && (
                      <div style={styles.infoRow}>
                        <span>📍</span><span style={styles.infoText}>{contact.meta.gpf_address}</span>
                      </div>
                    )}
                  </div>

                  {projIds.length > 0 && (
                    <div style={styles.cardProjects}>
                      {projIds.slice(0,2).map(id => (
                        <span key={id} style={styles.projTag}>📁 {getProjectName(id)}</span>
                      ))}
                      {projIds.length > 2 && <span style={styles.projTag}>+{projIds.length - 2} more</span>}
                    </div>
                  )}

                  <div style={styles.cardActions}>
                    <button style={styles.cardBtn} onClick={e => { e.stopPropagation(); openEdit(contact); }}>✏️ Edit</button>
                    <button style={{ ...styles.cardBtn, color: '#dc2626' }} onClick={e => { e.stopPropagation(); handleDelete(contact.id, name); }}>🗑 Delete</button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Contact Detail Drawer */}
        {viewContact && (
          <div style={styles.drawer}>
            <div style={styles.drawerHeader}>
              <div style={{ ...styles.avatar, width: 48, height: 48, fontSize: 18 }}>
                {getInitials(viewContact.title?.rendered || '')}
              </div>
              <div style={{ flex: 1 }}>
                <div style={styles.drawerName}>{viewContact.title?.rendered}</div>
                {viewContact.meta?.gpf_job_title && <div style={{ fontSize: 13, color: '#94a3b8' }}>{viewContact.meta.gpf_job_title}</div>}
              </div>
              <button onClick={() => setViewContact(null)} style={styles.drawerClose}>✕</button>
            </div>
            <div style={styles.drawerBody}>
              {(() => {
                const sc = getStatusStyle(viewContact.meta?.gpf_status || 'lead');
                return <span style={{ ...styles.statusBadge, background: sc.bg, color: sc.text, marginBottom: 16, display: 'inline-block' }}>{sc.label}</span>;
              })()}

              {viewContact.meta?.gpf_company && <div style={styles.drawerRow}><span style={styles.drawerIcon}>🏢</span><span>{viewContact.meta.gpf_company}</span></div>}
              {viewContact.meta?.gpf_email && <div style={styles.drawerRow}><span style={styles.drawerIcon}>✉️</span><a href={`mailto:${viewContact.meta.gpf_email}`} style={{ color: '#2563eb' }}>{viewContact.meta.gpf_email}</a></div>}
              {viewContact.meta?.gpf_phone && <div style={styles.drawerRow}><span style={styles.drawerIcon}>📞</span><a href={`tel:${viewContact.meta.gpf_phone}`} style={{ color: '#2563eb' }}>{viewContact.meta.gpf_phone}</a></div>}
              {viewContact.meta?.gpf_address && <div style={styles.drawerRow}><span style={styles.drawerIcon}>📍</span><span>{viewContact.meta.gpf_address}</span></div>}

              {(() => {
                const ids = JSON.parse(viewContact.meta?.gpf_project_ids || '[]');
                if (!ids.length) return null;
                return (
                  <div style={{ marginTop: 16 }}>
                    <p style={styles.drawerSection}>Linked Projects</p>
                    {ids.map(id => <div key={id} style={styles.projTagFull}>📁 {getProjectName(id)}</div>)}
                  </div>
                );
              })()}

              {viewContact.content?.rendered && (
                <div style={{ marginTop: 16 }}>
                  <p style={styles.drawerSection}>Notes</p>
                  <p style={{ fontSize: 13, color: '#475569', lineHeight: 1.7 }} dangerouslySetInnerHTML={{ __html: viewContact.content.rendered }} />
                </div>
              )}
            </div>
            <div style={styles.drawerFooter}>
              <button style={styles.cancelBtn} onClick={() => setViewContact(null)}>Close</button>
              <button style={styles.saveBtn} onClick={() => openEdit(viewContact)}>Edit Contact</button>
            </div>
          </div>
        )}
        {viewContact && <div style={styles.drawerOverlay} onClick={() => setViewContact(null)} />}
      </div>
    </Layout>
  );
}

const styles = {
  page:         { maxWidth: 1100, margin: '0 auto' },
  header:       { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 },
  title:        { fontSize: 28, fontWeight: 800, color: '#0f172a', margin: 0, letterSpacing: '-0.5px' },
  sub:          { fontSize: 14, color: '#64748b', margin: '4px 0 0' },
  addBtn:       { padding: '10px 20px', background: '#2563eb', color: 'white', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer' },
  filterBar:    { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 12 },
  tabs:         { display: 'flex', gap: 4, background: 'white', padding: 4, borderRadius: 10, boxShadow: '0 1px 4px rgba(0,0,0,0.06)', flexWrap: 'wrap' },
  tab:          { padding: '7px 14px', border: 'none', borderRadius: 7, background: 'transparent', fontSize: 13, fontWeight: 500, color: '#64748b', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 },
  tabActive:    { background: '#2563eb', color: 'white', fontWeight: 600 },
  tabCount:     { fontSize: 11, fontWeight: 700, padding: '1px 6px', borderRadius: 99, background: 'rgba(0,0,0,0.08)' },
  search:       { padding: '9px 14px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 14, outline: 'none', width: 280 },
  formCard:     { background: 'white', borderRadius: 12, padding: 24, marginBottom: 24, boxShadow: '0 1px 8px rgba(0,0,0,0.08)' },
  formHeader:   { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 },
  formTitle:    { fontSize: 17, fontWeight: 700, color: '#0f172a', margin: 0 },
  formClose:    { background: 'none', border: 'none', fontSize: 18, color: '#94a3b8', cursor: 'pointer' },
  errorBox:     { background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 13, color: '#dc2626' },
  formGrid:     { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 14, marginBottom: 20 },
  label:        { display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.04em' },
  input:        { width: '100%', padding: '9px 12px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 14, outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit' },
  projectPicker:{ display: 'flex', flexWrap: 'wrap', gap: 8 },
  projectCheck: { display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer', padding: '4px 8px', background: '#f8fafc', borderRadius: 6 },
  formActions:  { display: 'flex', justifyContent: 'flex-end', gap: 10 },
  cancelBtn:    { padding: '9px 20px', border: '1.5px solid #e2e8f0', borderRadius: 8, background: 'white', cursor: 'pointer', fontSize: 14 },
  saveBtn:      { padding: '9px 20px', background: '#2563eb', color: 'white', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer' },
  empty:        { textAlign: 'center', padding: '60px 0', background: 'white', borderRadius: 12 },
  emptyTitle:   { fontSize: 16, fontWeight: 600, color: '#374151', margin: '8px 0 16px' },
  grid:         { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 16 },
  card:         { background: 'white', borderRadius: 12, padding: 20, boxShadow: '0 1px 4px rgba(0,0,0,0.06)', cursor: 'pointer', border: '1px solid #f1f5f9', transition: 'box-shadow 0.15s' },
  cardTop:      { display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 14 },
  avatar:       { width: 40, height: 40, borderRadius: '50%', background: 'linear-gradient(135deg, #2563eb, #1d4ed8)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 700, flexShrink: 0 },
  contactName:  { fontSize: 15, fontWeight: 700, color: '#0f172a', lineHeight: 1.3 },
  contactRole:  { fontSize: 12, color: '#64748b', marginTop: 2 },
  contactCompany:{ fontSize: 12, color: '#94a3b8', marginTop: 2 },
  statusBadge:  { fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 99, flexShrink: 0 },
  cardInfo:     { display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 12 },
  infoRow:      { display: 'flex', alignItems: 'center', gap: 7, textDecoration: 'none' },
  infoText:     { fontSize: 13, color: '#475569', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  cardProjects: { display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 12 },
  projTag:      { fontSize: 11, padding: '2px 8px', background: '#eff6ff', color: '#2563eb', borderRadius: 99, fontWeight: 500 },
  projTagFull:  { fontSize: 13, padding: '6px 10px', background: '#eff6ff', color: '#2563eb', borderRadius: 8, marginBottom: 4 },
  cardActions:  { display: 'flex', gap: 8, borderTop: '1px solid #f1f5f9', paddingTop: 12 },
  cardBtn:      { flex: 1, padding: '7px', border: '1px solid #e2e8f0', borderRadius: 6, background: 'white', fontSize: 12, cursor: 'pointer', color: '#374151' },
  drawer:       { position: 'fixed', right: 0, top: 0, bottom: 0, width: 360, background: 'white', boxShadow: '-4px 0 24px rgba(0,0,0,0.12)', zIndex: 200, display: 'flex', flexDirection: 'column' },
  drawerHeader: { padding: '20px', display: 'flex', alignItems: 'center', gap: 14, borderBottom: '1px solid #f1f5f9', background: 'linear-gradient(135deg, #f8fafc, #eff6ff)' },
  drawerName:   { fontSize: 16, fontWeight: 700, color: '#0f172a' },
  drawerClose:  { background: 'none', border: 'none', fontSize: 18, color: '#94a3b8', cursor: 'pointer', flexShrink: 0 },
  drawerBody:   { flex: 1, overflowY: 'auto', padding: 20 },
  drawerRow:    { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, fontSize: 14, color: '#374151' },
  drawerIcon:   { fontSize: 16, flexShrink: 0 },
  drawerSection:{ fontSize: 12, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', margin: '0 0 8px' },
  drawerFooter: { padding: 16, borderTop: '1px solid #f1f5f9', display: 'flex', gap: 10, justifyContent: 'flex-end' },
  drawerOverlay:{ position: 'fixed', inset: 0, zIndex: 199 },
};